import React, { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { Button, Badge } from '@gu-corp/gu-ui';
import { truncateEthAddress } from '~/utils/string.utils';
import { getEnv } from '~/env';
import { useChainId, useSwitchChain, useWalletClient } from 'wagmi';
import { BrowserProvider } from 'ethers';
import { useToast } from '~/hooks/use-toast';
import { LendingRecord, useLendingPlanData } from '~/hooks/with-lending-data';
import { renderBalance } from '~/utils/render.util';
import moment from 'moment';
import { getLendingPlanDurationTitle } from '~/utils/lending.util';
import * as Sentry from '@sentry/nextjs';

interface Props {
  record: LendingRecord;
}

const DepositItem = (props: Props) => {
  const { t } = useTranslation('common');
  const { record } = props;
  const { switchChainAsync } = useSwitchChain();
  const { enqueueSnackbar } = useToast();
  const chainId = useChainId();
  const [isLoading, setLoading] = useState(false);
  const { data: walletClient } = useWalletClient();
  const { refetch: refetchLendingPlanData } = useLendingPlanData(record.plan);

  const openTransactionDetail = useCallback(() => {
    window.open(`${getEnv('NEXT_PUBLIC_EXPLORER_URL')}/tx/${record.lended.transactionHash}`);
  }, [record]);

  const handleWithdraw = useCallback(async () => {
    if (!walletClient) return;
    setLoading(true);
    try {
      if (chainId !== Number(getEnv('NEXT_PUBLIC_JOC_CHAIN_ID'))) {
        await switchChainAsync({ chainId: Number(getEnv('NEXT_PUBLIC_JOC_CHAIN_ID')) });
      }

      // Convert wagmi walletClient to ethers signer
      const provider = new BrowserProvider(walletClient.transport, walletClient.chain.id);
      const signer = await provider.getSigner();
      const tx = await record.contract(signer).withdraw(record.recordId);
      await tx.wait();

      enqueueSnackbar(t('lending_page.withdraw_successfully'), {
        variant: 'success',
      });

      refetchLendingPlanData();
    } catch (error: any) {
      Sentry.captureException(error);
      enqueueSnackbar(t('lending_page.withdraw_failed'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [record, chainId, switchChainAsync, walletClient, enqueueSnackbar, t, refetchLendingPlanData]);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-foreground" data-testid="deposit-item-plan-title">
              {getLendingPlanDurationTitle(record.plan, t)}
            </span>
            {record.withdrawn !== null && (
              <Badge variant="outline" className="gap-1" data-testid="deposit-item-returned-chip">
                <CheckCircle className="w-3 h-3" />
                {t('returned')}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>{`${t('start')}: ${moment(record.createdAt * 1000).format(
              t('date_time_format'),
            )}`}</span>
            <span>{`${t('end')}: ${moment(record.releasedAt * 1000).format(
              t('date_time_format'),
            )}`}</span>
            <span className="hidden sm:inline">{`${t('transaction')}: ${truncateEthAddress(
              record.lended.transactionHash,
            )}`}</span>
          </div>
        </div>

        <div className="text-left sm:text-right">
          <div className="text-lg font-semibold text-foreground">{`${renderBalance(
            record.amount,
          )} JOC`}</div>
          <div className="text-sm text-muted-foreground">{`${renderBalance(
            record.rewardAmount,
            6,
          )} USDTX ${t('received')}`}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={openTransactionDetail}
          data-testid={`deposit-item-${record.id}-view-transaction-button`}
        >
          {t('lending_page.view_transaction_detail')}
          <ExternalLink className="ml-2 h-4 w-4" />
        </Button>

        {record.withdrawn === null && Date.now() > record.releasedAt * 1000 && (
          <Button
            size="sm"
            onClick={handleWithdraw}
            disabled={isLoading}
            data-testid={`deposit-item-${record.id}-release-button`}
          >
            {t('release')}
            {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DepositItem;
